import React, { useState, useEffect, useRef } from 'react';
import { Search, ChevronDown, Check } from 'lucide-react';

const SearchableSelect = ({ 
  value, 
  onChange, 
  options, 
  placeholder = "Sélectionner...",
  label,
  required = false,
  displayKey = 'nom', // Clé pour afficher le nom
  searchKeys = ['nom', 'prenom'], // Clés pour la recherche
  renderOption = null // Fonction custom pour afficher une option
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const containerRef = useRef(null);

  // Filtrer les options selon la recherche
  const filteredOptions = options.filter(option => {
    const searchLower = searchTerm.toLowerCase();
    return searchKeys.some(key => 
      option[key]?.toString().toLowerCase().includes(searchLower)
    );
  });

  // Trouver l'option sélectionnée
  const selectedOption = options.find(opt => opt.id?.toString() === value?.toString());

  // Fermer au clic extérieur
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelect = (option) => {
    onChange(option.id.toString());
    setIsOpen(false);
    setSearchTerm('');
  };

  // Affichage par défaut d'une option
  const defaultRenderOption = (option) => {
    if (option.nom && option.prenom) {
      return `${option.nom} ${option.prenom}`;
    }
    return option[displayKey];
  };

  const renderOptionContent = renderOption || defaultRenderOption;

  return (
    <div ref={containerRef} className="relative">
      {label && (
        <label className="block text-sm font-semibold text-gray-700 mb-2">
          {label}
          {required && <span className="text-red-500 ml-1">*</span>}
        </label>
      )}

      {/* Bouton de sélection */}
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className={`w-full px-4 py-3 border-2 rounded-xl flex items-center justify-between transition-colors ${
          isOpen 
            ? 'border-[#319190] bg-white' 
            : 'border-gray-200 hover:border-gray-300 bg-white'
        }`}
      >
        <span className={selectedOption ? 'text-gray-900' : 'text-gray-400'}>
          {selectedOption ? renderOptionContent(selectedOption) : placeholder}
        </span>
        <ChevronDown 
          className={`w-5 h-5 text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} 
        />
      </button>

      {/* Liste déroulante */}
      {isOpen && (
        <div className="absolute z-50 w-full mt-2 bg-white border-2 border-gray-200 rounded-xl shadow-xl overflow-hidden">
          {/* Barre de recherche */}
          <div className="p-3 border-b border-gray-200 bg-gray-50">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder="Rechercher..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:border-[#319190] focus:outline-none text-sm"
                autoFocus
              />
            </div>
          </div>

          {/* Liste des options */}
          <div className="max-h-60 overflow-y-auto">
            {filteredOptions.length === 0 ? (
              <div className="p-4 text-center text-gray-500 text-sm">
                Aucun résultat trouvé
              </div>
            ) : (
              filteredOptions.map((option) => {
                const isSelected = option.id?.toString() === value?.toString();
                
                return (
                  <button
                    key={option.id}
                    type="button"
                    onClick={() => handleSelect(option)}
                    className={`w-full px-4 py-3 text-left hover:bg-gray-50 transition-colors flex items-center justify-between ${
                      isSelected ? 'bg-[#319190]/5' : ''
                    }`}
                  >
                    <span className={`text-sm ${isSelected ? 'font-semibold text-[#319190]' : 'text-gray-900'}`}>
                      {renderOptionContent(option)}
                    </span>
                    {isSelected && (
                      <Check className="w-5 h-5 text-[#319190]" />
                    )}
                  </button>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default SearchableSelect;